import { createSignal, type Accessor } from 'solid-js'
import { logProductPurchaseSuccess } from '~/lib/analytics'
import type { CatalogueCurrency } from '~/lib/catalogue-price'
import { isRefreshLocked } from '~/lib/countdown'
import { logNonFatal } from '~/lib/diagnostics'
import { BREATH_CYCLE_MS, flipSpanMs } from '~/lib/refresh-reveal'
import { errorDetail, type ShopBootstrap } from '~/services/bootstrap'
import type { RefreshTicket } from '~/services/refresh-tickets'
import {
  classifyShopPurchaseFailure,
  purchaseShopProduct,
} from '~/services/shop-purchase'
import {
  loadReplacementDailyPicksGrid,
  type DailyPicksGrid,
} from './daily-picks-grid'

// The Daily Picks refresh flow (GLOSSARY.md "Daily Picks refresh", ADR 0032):
// one operation spanning the refresh ticket's purchase and the daily-pick
// refetch, under one inline pending state. It runs beside the purchase-flow
// machine rather than through it — that machine's locked confirmation and
// processing overlay both present a pending purchase in the foreground,
// while this one's outcome is the grid behind the dismissed dialog.
//
// The ticket is captured when the confirmation opens, so the price the user
// agreed to is the one submitted, the one debited, and the one the dialog
// keeps showing however the tier moves underneath it.
//
// Every failure but a balance shortage closes Shop (ADR 0032), so the two
// error phases are pending phases: the button keeps spinning and the grid
// stays locked beneath the dialog, because there is no journey left to
// return to. Only the shortage leaves Shop open, and it goes back to idle.
export type DailyPicksRefreshFlowPhase =
  | { status: 'confirming'; ticket: RefreshTicket }
  // The purchase leg's timeout or network error: the outcome is unknown and
  // the terminal tier is a repeatable product, so the connection issue
  // dialog's OK closes Shop rather than inviting a second charge.
  | { status: 'connection' }
  // Anything else that went wrong, under the code the dialog shows: a
  // purchase-leg rejection, or any failure of the refetch after the
  // purchase committed.
  | { code: string; status: 'error' }
  // The tap landed on an exhausted pool (GLOSSARY.md): every unowned good
  // left is already on the shelf, so the Nothing-new dialog says so and
  // nothing is bought. No ticket: its copy names neither currency nor price.
  | { status: 'exhausted' }
  | { status: 'idle' }
  // The tap landed inside the last-minute refresh lock (GLOSSARY.md): the
  // one-minute dialog explains the rollover is about to deliver the same
  // rotation for free, and nothing is bought.
  | { status: 'locked'; ticket: RefreshTicket }
  | { status: 'purchasing'; ticket: RefreshTicket }
  | { status: 'refetching'; ticket: RefreshTicket }
  // The new assortment is on screen face-down and turning face-up
  // (GLOSSARY.md "Refresh reveal"): the last leg of the pending state, over
  // once the last card has landed.
  | { status: 'revealing' }

// The server's verdict that the tier costs more than the user has
// (GLOSSARY.md "Insufficient purchase balance"). A distinct object per
// shortage, so the navigation it asks for fires again for a second shortage
// in the same currency rather than being swallowed by signal equality.
export interface RefreshShortage {
  currency: CatalogueCurrency
}

// The refresh button's tap: the tier it quoted, and whether the pool on
// screen is exhausted (GLOSSARY.md "Exhausted pool"). One type, because it
// travels from the button through the catalogue and the shell to the flow.
export type DailyPicksRefreshTap = (
  ticket: RefreshTicket,
  context: { exhausted: boolean },
) => void

export interface DailyPicksRefreshFlow {
  // The reveal's breathing beat (GLOSSARY.md "Refresh reveal"): true from
  // the moment the whole grid has turned face-down until the new assortment
  // is dealt, so the "?" cards pulse while the server is awaited. Whole
  // breaths only — the beat ends at a cycle's end, never mid-pulse.
  breathing: Accessor<boolean>
  cancel: () => void
  confirm: () => void
  dismiss: (phase: DailyPicksRefreshFlowPhase) => void
  // The face every Daily Picks card shows (GLOSSARY.md "Refresh reveal"):
  // face-down from the commit until the new assortment is on screen, then
  // face-up. It outlives the flip itself — a card mounting after a tab
  // switch reads it to arrive on the right face.
  faceDown: Accessor<boolean>
  // Inside the last minute before the rollover (GLOSSARY.md "Last-minute
  // refresh lock"): the button dims on it, and `open` answers a tap with the
  // one-minute dialog instead of a sale. One owner for the rule, read
  // reactively by the look and by the tap.
  locked: Accessor<boolean>
  // The button's tap. Exhaustion is the button's to know — it is judged
  // over the grid on screen — so the tap carries it in; the lock is the
  // flow's own. The precedence between them lives here, in one place.
  open: DailyPicksRefreshTap
  // The inline pending state: the button spins and the grid stops taking
  // taps from the moment the confirmation is accepted until the new grid is
  // in place — or until an error dialog closes Shop over it.
  pending: Accessor<boolean>
  phase: Accessor<DailyPicksRefreshFlowPhase>
  // The button's own check thought the balance enough and the server
  // disagreed: an HTTP 422 asks for the same currency-aware top-up
  // navigation an unaffordable tier does.
  shortage: Accessor<RefreshShortage | undefined>
}

const IDLE: DailyPicksRefreshFlowPhase = { status: 'idle' }

// The reveal's beats are timers, so they run identically whether the cards
// animated or swapped.
const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })

export function createDailyPicksRefreshFlow(input: {
  bootstrap: Accessor<ShopBootstrap | undefined>
  // How many slots the Daily Picks grid on screen has: how long that
  // assortment takes to turn face-down, which its hold waits out.
  dailyPickSlotCount: Accessor<number>
  // The commit boundary (ADR 0032): the ledger debit and the refresh count's
  // increment, at the captured ticket's price.
  onCommitted: (ticket: RefreshTicket) => void
  onSwap: (grid: DailyPicksGrid) => void
  serverNow: Accessor<number>
}): DailyPicksRefreshFlow {
  const [phase, setPhase] = createSignal<DailyPicksRefreshFlowPhase>(IDLE)
  const [faceDown, setFaceDown] = createSignal(false)
  const [breathing, setBreathing] = createSignal(false)
  const [shortage, setShortage] = createSignal<RefreshShortage>()
  const locked = () => isRefreshLocked(input.serverNow())

  const run = async (ticket: RefreshTicket, data: ShopBootstrap) => {
    try {
      await purchaseShopProduct(data.currentUser.id, ticket.productId)
    } catch (cause) {
      const failure = classifyShopPurchaseFailure(cause)
      if (failure.kind === 'insufficient') {
        // The one error exit that leaves Shop open: nothing was debited or
        // counted, so the flow goes back to idle and the button is live
        // again on the way back from Coins & Crystals.
        setPhase(IDLE)
        setShortage({ currency: ticket.currency })
        return
      }
      setPhase(
        failure.kind === 'connection'
          ? { status: 'connection' }
          : { code: failure.code, status: 'error' },
      )
      return
    }
    logProductPurchaseSuccess(ticket.productId)
    input.onCommitted(ticket)

    // The refresh reveal opens on the commit (GLOSSARY.md): the assortment
    // the user has just paid to replace turns face-down at once, and its
    // hold starts here — not when the refetch answers — so a fast server
    // still reads as a flip.
    //
    // Nothing below undoes this: a failed refetch keeps the grid in whatever
    // state the reveal left it, face-down included, beneath an error dialog
    // whose only exit closes Shop (ADR 0032's error contract — its wording
    // was clarified in this slice, because "no face-down grid left behind"
    // reads as forbidding the face rather than the shelf it leaves).
    setFaceDown(true)
    // The hold, in beats: once the last card has landed face-down the grid
    // breathes, and keeps breathing in whole cycles until the new assortment
    // has settled — at least one, so a server that has already answered
    // still reads as a flip rather than a flicker, and never a fraction, so
    // the deal never starts mid-pulse. It runs beside the refetch, and
    // outlives a failed one only to the end of the cycle it is in: the
    // cards stay breathing beneath the error dialog.
    let settled = false
    const held = (async () => {
      await wait(flipSpanMs(input.dailyPickSlotCount()))
      setBreathing(true)
      do await wait(BREATH_CYCLE_MS)
      while (!settled)
    })()

    setPhase({ status: 'refetching', ticket })
    let grid: DailyPicksGrid
    try {
      // The rollover's refetch, reused whole (ADR 0032): the two share the
      // grid, and whichever settles last wins.
      grid = await loadReplacementDailyPicksGrid(data)
    } catch (cause) {
      // The refetch leg is one labeling point like the rollover's: every
      // failure after the commit — either fetch leg, a response without the
      // collection, or anything after — reaches the general error dialog
      // under the refresh-identifying code, and closes Shop so the next
      // open reads the assortment the user has already paid for.
      settled = true
      const code = `daily_pick_refresh: ${errorDetail(cause)}`
      logNonFatal('shop_daily_pick_refresh_failed', { detail: code })
      setPhase({ code, status: 'error' })
      return
    }
    settled = true
    await held

    // The breath ends at the peak it began from, so the cards that leave
    // and the cards that arrive meet on the same face at rest.
    setBreathing(false)
    setPhase({ status: 'revealing' })
    // Order carries the reveal: the new cards mount into a face-down grid,
    // so they arrive on the "?" face at their own slot count, and the write
    // that follows is the change each one turns on.
    input.onSwap(grid)
    setFaceDown(false)
    // The pending state ends with the last slot's turn, not with the swap —
    // the button must not invite a second refresh over a half-dealt hand.
    // Slots, not cards: an omitted slot keeps its place in the rhythm, so a
    // trailing one holds the button a stagger step past the last landing.
    // This span is the new assortment's, as the hold's was the old one's:
    // the two grids may be different sizes. The wait is on the timer either
    // way — cards that swapped without animating (reduced motion, no Web
    // Animations API) still land on this beat.
    await wait(flipSpanMs(grid.slots.length))
    setPhase(IDLE)
  }

  return {
    breathing,
    cancel: () => {
      setPhase(IDLE)
    },
    // Accepting the confirmation dismisses it at once and enters the inline
    // pending state; the purchase and the refetch run on behind the grid.
    confirm: () => {
      const current = phase()
      const data = input.bootstrap()
      if (current.status !== 'confirming' || !data) return
      setPhase({ status: 'purchasing', ticket: current.ticket })
      void run(current.ticket, data)
    },
    // The confirmation went away without a decision — the shared dialog is
    // a singleton, so another dialog opening over it (the rollover's general error dialog,
    // say) closes it. Only a flow still waiting on that very confirmation
    // returns to idle; without this the button would stay tappable with
    // every tap a no-op.
    dismiss: (dismissed) => {
      if (phase() === dismissed) setPhase(IDLE)
    },
    faceDown,
    locked,
    // Single-flight (ADR 0032): the disabled button is the lock, and a tap
    // that slips past it while anything is in flight does nothing.
    //
    // The exhausted pool and the last-minute refresh lock (GLOSSARY.md) are
    // judged on the way in and nowhere else: a confirmation already open and
    // a purchase already submitted both run their course untouched, while a
    // tap seconds later is answered with a dialog instead of a sale. The
    // pool comes first — it stays exhausted past the rollover the lock
    // announces, so "free in a minute" would be the wrong answer over it.
    open: (ticket, context) => {
      if (phase().status !== 'idle') return
      if (context.exhausted) {
        setPhase({ status: 'exhausted' })
        return
      }
      setPhase(
        locked()
          ? { status: 'locked', ticket }
          : { status: 'confirming', ticket },
      )
    },
    phase,
    // The phases that hold the button's spinner and the grid's lock. The
    // dialog phases the user still has to answer — `confirming`, `locked`,
    // and `exhausted` — leave the grid alone, because none has bought
    // anything yet; the two error phases keep it, because there is no
    // journey left to return to (ADR 0032). Exhaustive on purpose: a phase
    // added to the union fails to type here rather than defaulting into the
    // wrong answer, which either a positive or a negative list would do
    // silently.
    pending: () => {
      switch (phase().status) {
        case 'connection':
        case 'error':
        case 'purchasing':
        case 'refetching':
        case 'revealing':
          return true
        case 'confirming':
        case 'exhausted':
        case 'idle':
        case 'locked':
          return false
      }
    },
    shortage,
  }
}
